import React, { useEffect, useState, useCallback } from 'react';
import {
  useParams
} from 'react-router-dom';
import api from '../api/api';
import CardLayoutTheme from '../theme/CardLayoutTheme'; // Import CardLayoutTheme
import {
  useTheme
} from '../context/ThemeContext'; // Import useTheme hook
import {
  useAuth
} from '../context/AuthContext'; // Import useAuth hook
import { mapStatusToBackend } from '../utils/statusMapping';
import toast from 'react-hot-toast';
import SpiceLevelBadge from '../components/SpiceLevelBadge';
import MenuItemImage from '../components/MenuItemImage';
import { DisplayContext, formatOrderId, getOrderTypeDisplay } from '../utils/orderFormatter';
import { CURRENCY } from '../config/currency';

interface OrderItem {
  id: number;
  menuItem: { name: string; price?: number };
  quantity: number;
  price: number;
  portionName?: string; // e.g., "LARGE", "HALF", "FULL"
  spiceLevel?: string; // e.g., "MILD", "MEDIUM", "HOT"
  comment?: string;
  toppings?: { id: number; name: string; price?: number }[];
  kotNumber?: string;
  kitchenStatus?: string;
}

interface KOTItem {
  id: number;
  itemName: string;
  quantity: number;
  specialInstructions?: string;
  status: string;
}

interface KOT {
  id: number;
  kotNumber: string;
  orderId: number;
  kitchenId: number;
  kitchenName: string;
  status: string;
  priority: string;
  tableNumber?: string;
  customerName?: string;
  specialInstructions?: string;
  createdAt: string;
  printedAt?: string;
  acknowledgedAt?: string;
  startedAt?: string;
  readyAt?: string;
  servedAt?: string;
  printCount: number;
  items: KOTItem[];
}

interface Order {
  id: number;
  status: string;
  items: OrderItem[];
  user: { username: string; id: number }; // Added user ID
  createdAt: string; // Added createdAt
  total: number; // Added total
  deliveryType: string; // Added deliveryType
  orderNotes?: string; // Order-level special instructions
  allergyNote?: string; // Allergy declaration — highlighted (UK plan Phase 4)
  // Timestamp fields for time tracking
  acceptedAt?: string;
  preparingAt?: string;
  readyAt?: string;
  completedAt?: string;
}

interface OrderAnalytics {
  orderToAcceptedMinutes?: number;
  orderToAcceptedTime?: string;
  acceptedToPreparingMinutes?: number;
  acceptedToPreparingTime?: string;
  preparingToReadyMinutes?: number;
  preparingToReadyTime?: string;
  readyToCompletedMinutes?: number;
  readyToCompletedTime?: string;
  totalMinutes?: number;
  totalTime?: string;
  timestamps: {
    createdAt?: string;
    acceptedAt?: string;
    preparingAt?: string;
    readyAt?: string;
    completedAt?: string;
  };
}

interface AuditLogEntry {
  id: number;
  orderId: number;
  eventType: string;
  eventTypeDisplay: string;
  performedById?: number;
  performedByName: string;
  performedByRole: string;
  description: string;
  previousValue?: string;
  newValue?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  icon: string;
}

const VendorOrderDetail: React.FC = () => {
  const { theme } = useTheme(); // Get the theme
  const { user } = useAuth(); // Get the authenticated user
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [analytics, setAnalytics] = useState<OrderAnalytics | null>(null);
  const [auditHistory, setAuditHistory] = useState<AuditLogEntry[]>([]);
  const [kots, setKots] = useState<KOT[]>([]);
  const [expandedKots, setExpandedKots] = useState<Set<number>>(new Set());
  const [newStatus, setNewStatus] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<boolean>(false); // Renamed loading to actionLoading for clarity
  const [pageLoading, setPageLoading] = useState<boolean>(true); // For initial page load
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(() => {
    if (orderId && user?.id) {
      api.get<OrderAnalytics>(`/vendors/${user.id}/orders/${orderId}/analytics`)
        .then((res) => {
          setAnalytics(res.data);
        })
        .catch((err: any) => {
          console.error("Failed to fetch analytics:", err);
          // Don't set error state for analytics failure, just log it
        });
    }
  }, [orderId, user]);

  const fetchKOTs = useCallback(() => {
    if (orderId) {
      api.get<KOT[]>(`/api/kot/order/${orderId}`)
        .then((res) => {
          setKots(res.data);
        })
        .catch((err: any) => {
          console.error("Failed to fetch KOTs:", err);
          // Don't set error state for KOTs failure, just log it
        });
    }
  }, [orderId]);

  const fetchAuditHistory = useCallback(() => {
    if (orderId) {
      api.get<AuditLogEntry[]>(`/api/orders/${orderId}/audit-history`)
        .then((res) => {
          setAuditHistory(res.data);
        })
        .catch((err: any) => {
          console.error("Failed to fetch audit history:", err);
          // Don't set error state for audit history failure, just log it
        });
    }
  }, [orderId]);

  const fetchOrderDetails = useCallback(() => {
    if (orderId) {
      if (!order) setPageLoading(true); // Show page loading only if order isn't already set (e.g. polling)
      api.get<Order>(`/orders/${orderId}`)
        .then((res) => {
          setOrder(res.data);
          setNewStatus(res.data.status); // Initialize newStatus with current order status
          setError(null); // Clear previous errors on successful fetch
          // Fetch analytics, KOTs, and audit history after getting order details
          fetchAnalytics();
          fetchKOTs();
          fetchAuditHistory();
        })
        .catch((err: any) => {
          console.error("Failed to fetch order:", err);
          setError(err.response?.data?.message || "Failed to load order details.");
        })
        .finally(() => {
          setPageLoading(false);
        });
    } else {
      setError("Order ID is missing.");
      setPageLoading(false);
    }
  }, [orderId, fetchAnalytics, fetchKOTs, fetchAuditHistory]);

  useEffect(() => {
    fetchOrderDetails(); // Initial fetch
  }, [fetchOrderDetails]);


  const toggleKOT = (kotId: number) => {
    const newExpanded = new Set(expandedKots);
    if (newExpanded.has(kotId)) {
      newExpanded.delete(kotId);
    } else {
      newExpanded.add(kotId);
    }
    setExpandedKots(newExpanded);
  };

  const getKOTStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'ACKNOWLEDGED':
        return 'bg-blue-100 text-blue-800';
      case 'PREPARING':
        return 'bg-orange-100 text-orange-800';
      case 'READY':
        return 'bg-green-100 text-green-800';
      case 'SERVED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatusUpdate = async () => {
    if (!order) return;
    setActionLoading(true);
    setError(null);
    try {
      // Map frontend status to backend status (ACKNOWLEDGED → ACCEPTED)
      const backendStatus = mapStatusToBackend(newStatus);
      console.log('VendorOrderDetail: Mapped status for backend:', { frontend: newStatus, backend: backendStatus });
      const response = await api.put<Order>(`/orders/${order.id}/status`, { status: backendStatus });
      setOrder(response.data);
      // Refresh analytics and audit history after status update
      fetchAnalytics();
      fetchAuditHistory();
      // setNewStatus(response.data.status); // Already set by fetchOrderDetails or onChange
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to update order status.");
    } finally {
      setActionLoading(false);
    }
  };

  const orderStatuses = [
    'PENDING', 'ACCEPTED', 'IN_PROGRESS', 'PREPARING', 'READY', 'COMPLETED', 'FAILED', 'CANCELLED'
  ];


  const renderVendorOrderContent = () => {
    if (pageLoading && !order) {
      return <p className="text-center text-gray-500 dark:text-gray-400 py-8">Loading order details...</p>;
    }

    if (error && !order) { // Show critical error if order couldn't be loaded at all
      return <p className="p-4 bg-red-100 dark:bg-red-800 border border-red-400 dark:border-red-600 text-red-700 dark:text-red-200 rounded-md text-center">{error}</p>;
    }

    if (!order) {
      return <p className="text-center text-gray-500 dark:text-gray-400 py-8">Order data is unavailable.</p>;
    }


    return (
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 md:p-8">
        {/* Order Header */}
        <div className="pb-6 border-b border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-2">
            <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2" style={{ color: theme.primary }}>
              <span className="text-2xl" style={{ color: getOrderTypeDisplay(order.customOrderId).color }}>
                {getOrderTypeDisplay(order.customOrderId).icon}
              </span>
              <span>Order {formatOrderId(order.customOrderId || `#${order.id}`, DisplayContext.VENDOR)}</span>
            </h2>
            <span className="px-3 py-1.5 text-sm font-semibold rounded-full"
              style={{
                backgroundColor: order.status === 'PENDING' ? theme.warning :
                  order.status === 'ACCEPTED' ? theme.primary :
                    order.status === 'PREPARING' ? theme.accent :
                      order.status === 'READY_FOR_PICKUP' ? theme.success :
                        order.status === 'COMPLETED' ? theme.secondary :
                          order.status === 'CANCELLED' ? theme.danger :
                            theme.gray?.[200] || '#E5E7EB',
                color: theme.text
              }}>
              Status: {order.status}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
            <p><span className="font-semibold">Customer:</span> {order.user?.username || 'Unknown'} (ID: {order.user?.id || 'N/A'})</p>
            <p><span className="font-semibold">Order Date:</span> {new Date(order.createdAt || Date.now()).toLocaleString()}</p>
            <p><span className="font-semibold">Delivery Type:</span> {order.deliveryType?.replace('_', ' ').toLowerCase()}</p>
            <p className="font-semibold text-gray-800 dark:text-white">Order Total: {CURRENCY.symbol}{(order.total || order.items.reduce((sum, i) => sum + i.price * i.quantity, 0)).toFixed(2)}</p>
          </div>
        </div>

        {/* Time Tracking Section */}
        {analytics && (
          <section className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-xl border border-blue-200 dark:border-gray-600">
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-6 flex items-center">
              <span className="mr-2">⏱️</span>
              Order Time Analytics
            </h3>

            <div className="space-y-6">
              {/* Duration Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analytics.orderToAcceptedTime && (
                  <div className="bg-white dark:bg-gray-600 p-4 rounded-lg shadow-sm">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Order to Accepted</p>
                    <p className="text-lg font-bold" style={{ color: theme.primary }}>{analytics.orderToAcceptedTime}</p>
                  </div>
                )}
                {analytics.acceptedToPreparingTime && (
                  <div className="bg-white dark:bg-gray-600 p-4 rounded-lg shadow-sm">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Accepted to Preparing</p>
                    <p className="text-lg font-bold" style={{ color: theme.primary }}>{analytics.acceptedToPreparingTime}</p>
                  </div>
                )}
                {analytics.preparingToReadyTime && (
                  <div className="bg-white dark:bg-gray-600 p-4 rounded-lg shadow-sm">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Preparing to Ready</p>
                    <p className="text-lg font-bold" style={{ color: theme.primary }}>{analytics.preparingToReadyTime}</p>
                  </div>
                )}
                {analytics.readyToCompletedTime && (
                  <div className="bg-white dark:bg-gray-600 p-4 rounded-lg shadow-sm">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Ready to Completed</p>
                    <p className="text-lg font-bold" style={{ color: theme.primary }}>{analytics.readyToCompletedTime}</p>
                  </div>
                )}
                {analytics.totalTime && (
                  <div className="bg-white dark:bg-gray-600 p-4 rounded-lg shadow-sm md:col-span-2">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Total Order Time</p>
                    <p className="text-2xl font-bold" style={{ color: theme.success }}>{analytics.totalTime}</p>
                  </div>
                )}
              </div>

              {/* Order Timeline - Audit Trail */}
              <div className="bg-white dark:bg-gray-600 p-6 rounded-lg shadow-sm">
                <h4 className="text-lg font-semibold mb-4 flex items-center" style={{ color: theme.primary }}>
                  <span className="mr-2">📅</span>
                  Order Timeline
                </h4>
                <div className="space-y-4">
                  {auditHistory.length > 0 ? (
                    auditHistory.map((event, index) => {
                      // Get background color based on event type
                      const getEventBgColor = (eventType: string) => {
                        switch (eventType) {
                          case 'ORDER_CREATED': return 'bg-blue-100 dark:bg-blue-900';
                          case 'STATUS_CHANGED': return 'bg-green-100 dark:bg-green-900';
                          case 'ITEM_ADDED': return 'bg-purple-100 dark:bg-purple-900';
                          case 'ITEM_REMOVED': return 'bg-red-100 dark:bg-red-900';
                          case 'ITEM_CANCELLED': return 'bg-red-100 dark:bg-red-900';
                          case 'PAYMENT_RECEIVED': return 'bg-emerald-100 dark:bg-emerald-900';
                          case 'KOT_PRINTED': return 'bg-orange-100 dark:bg-orange-900';
                          case 'ORDER_CANCELLED': return 'bg-red-100 dark:bg-red-900';
                          case 'INVOICE_GENERATED': return 'bg-cyan-100 dark:bg-cyan-900';
                          default: return 'bg-gray-100 dark:bg-gray-700';
                        }
                      };

                      const getBadgeColor = (eventType: string) => {
                        switch (eventType) {
                          case 'ORDER_CREATED': return 'bg-blue-100 text-blue-800';
                          case 'STATUS_CHANGED': return 'bg-green-100 text-green-800';
                          case 'ITEM_ADDED': return 'bg-purple-100 text-purple-800';
                          case 'ITEM_REMOVED': return 'bg-red-100 text-red-800';
                          case 'ITEM_CANCELLED': return 'bg-red-100 text-red-800';
                          case 'PAYMENT_RECEIVED': return 'bg-emerald-100 text-emerald-800';
                          case 'KOT_PRINTED': return 'bg-orange-100 text-orange-800';
                          case 'ORDER_CANCELLED': return 'bg-red-100 text-red-800';
                          case 'INVOICE_GENERATED': return 'bg-cyan-100 text-cyan-800';
                          default: return 'bg-gray-100 text-gray-800';
                        }
                      };

                      const getRoleBadgeColor = (role: string) => {
                        switch (role?.toUpperCase()) {
                          case 'VENDOR': return 'bg-indigo-100 text-indigo-800';
                          case 'STEWARD': return 'bg-amber-100 text-amber-800';
                          case 'KITCHEN': return 'bg-orange-100 text-orange-800';
                          case 'ADMIN': return 'bg-red-100 text-red-800';
                          case 'GUEST': return 'bg-gray-100 text-gray-800';
                          case 'SYSTEM': return 'bg-slate-100 text-slate-800';
                          default: return 'bg-gray-100 text-gray-800';
                        }
                      };

                      return (
                        <div key={event.id} className="relative flex items-start space-x-4">
                          {/* Timeline connector line */}
                          {index < auditHistory.length - 1 && (
                            <div className="absolute left-5 top-10 w-0.5 h-full bg-gray-200 dark:bg-gray-500" />
                          )}

                          {/* Icon */}
                          <div className={`flex-shrink-0 w-10 h-10 ${getEventBgColor(event.eventType)} rounded-full flex items-center justify-center z-10`}>
                            <span className="text-lg">{event.icon}</span>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 pb-4">
                            <div className="flex items-center flex-wrap gap-2 mb-1">
                              <p className="text-base font-semibold" style={{ color: theme.text }}>
                                {event.eventTypeDisplay}
                              </p>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBadgeColor(event.eventType)}`}>
                                {event.eventType.replace(/_/g, ' ')}
                              </span>
                            </div>

                            {/* Description */}
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                              {event.description}
                            </p>

                            {/* State change (if applicable) */}
                            {event.previousValue && event.newValue && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <span className="line-through text-red-500">{event.previousValue}</span>
                                <span className="mx-1">→</span>
                                <span className="text-green-600 font-medium">{event.newValue}</span>
                              </p>
                            )}

                            {/* Performer and timestamp row */}
                            <div className="flex items-center flex-wrap gap-2 text-sm">
                              <span className="text-gray-500 dark:text-gray-400">
                                {new Date(event.createdAt).toLocaleString()}
                              </span>
                              <span className="text-gray-400">•</span>
                              <span className="font-medium text-gray-700 dark:text-gray-300">
                                {event.performedByName}
                              </span>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(event.performedByRole)}`}>
                                {event.performedByRole}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    /* Fallback to basic timestamps if no audit history */
                                    <>
                                      {analytics.timestamps.createdAt && (
                                        <div className="flex items-start gap-2 sm:gap-3">
                                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                                            <span className="text-sm sm:text-lg">📝</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                              <p className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>Order Placed</p>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-blue-100 text-blue-800 whitespace-nowrap">
                                                Created
                                              </span>
                                            </div>
                                            <p className="text-xs sm:text-sm" style={{ color: theme.gray[500] }}>
                                              {new Date(analytics.timestamps.createdAt).toLocaleString()}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {analytics.timestamps.acceptedAt && (
                                        <div className="flex items-start gap-2 sm:gap-3">
                                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                                            <span className="text-sm sm:text-lg">✅</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                              <p className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>Order Accepted</p>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                                                Accepted
                                              </span>
                                            </div>
                                            <p className="text-xs sm:text-sm" style={{ color: theme.gray[500] }}>
                                              {new Date(analytics.timestamps.acceptedAt).toLocaleString()}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {analytics.timestamps.preparingAt && (
                                        <div className="flex items-start gap-2 sm:gap-3">
                                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
                                            <span className="text-sm sm:text-lg">👨‍🍳</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                              <p className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>Started Preparing</p>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap">
                                                Preparing
                                              </span>
                                            </div>
                                            <p className="text-xs sm:text-sm" style={{ color: theme.gray[500] }}>
                                              {new Date(analytics.timestamps.preparingAt).toLocaleString()}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {analytics.timestamps.readyAt && (
                                        <div className="flex items-start gap-2 sm:gap-3">
                                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                                            <span className="text-sm sm:text-lg">🔔</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                              <p className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>Order Ready</p>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
                                                Ready
                                              </span>
                                            </div>
                                            <p className="text-xs sm:text-sm" style={{ color: theme.gray[500] }}>
                                              {new Date(analytics.timestamps.readyAt).toLocaleString()}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                      {analytics.timestamps.completedAt && (
                                        <div className="flex items-start gap-2 sm:gap-3">
                                          <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                                            <span className="text-sm sm:text-lg">🎉</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                              <p className="text-sm sm:text-base font-semibold" style={{ color: theme.text }}>Order Completed</p>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-100 text-emerald-800 whitespace-nowrap">
                                                Completed
                                              </span>
                                            </div>
                                            <p className="text-xs sm:text-sm" style={{ color: theme.gray[500] }}>
                                              {new Date(analytics.timestamps.completedAt).toLocaleString()}
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* KOT Section */}
        <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">Kitchen Order Tickets (KOTs)</h3>
          {kots.length > 0 ? (
            <div className="space-y-4">
              {kots.map((kot) => (
                <div key={kot.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm overflow-hidden">
                  {/* KOT Header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => toggleKOT(kot.id)}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      <span className="text-base sm:text-lg mt-0.5 flex-shrink-0">{expandedKots.has(kot.id) ? '▼' : '▶'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                          <p className="font-semibold text-gray-800 dark:text-white text-sm sm:text-base">
                            KOT #{kot.kotNumber}
                          </p>
                          <span className="text-xs sm:text-sm text-gray-500">- {kot.kitchenName}</span>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap ${getKOTStatusColor(kot.status)}`}>
                            {kot.status}
                          </span>
                          {kot.priority !== 'NORMAL' && (
                            <span className="px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-red-100 text-red-800 whitespace-nowrap">
                              {kot.priority}
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          Created: {new Date(kot.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* KOT Items (Expandable) */}
                  {expandedKots.has(kot.id) && (
                    <div className="border-t border-gray-200 dark:border-gray-600 p-4">
                      {/* KOT Timestamps */}
                      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                        {kot.acknowledgedAt && (
                          <p>Acknowledged: {new Date(kot.acknowledgedAt).toLocaleString()}</p>
                        )}
                        {kot.startedAt && (
                          <p>Started: {new Date(kot.startedAt).toLocaleString()}</p>
                        )}
                        {kot.readyAt && (
                          <p>Ready: {new Date(kot.readyAt).toLocaleString()}</p>
                        )}
                        {kot.servedAt && (
                          <p>Served: {new Date(kot.servedAt).toLocaleString()}</p>
                        )}
                      </div>

                      {/* KOT Items */}
                      <div className="space-y-3">
                        {kot.items.map((item) => (
                          <div
                            key={item.id}
                            className={`pl-4 border-l-4 ${
                              item.status === 'CANCELLED'
                                ? 'border-red-400 bg-red-50 dark:bg-red-900/20 rounded-r-lg py-2 pr-2'
                                : 'border-gray-300 dark:border-gray-500'
                            }`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  {(item as any).imageUrl && (
                                    <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0">
                                      <MenuItemImage imageUrl={(item as any).imageUrl} itemName={item.itemName} size="thumbnail" />
                                    </div>
                                  )}
                                  <p className={`font-medium ${
                                    item.status === 'CANCELLED'
                                      ? 'text-red-500 line-through'
                                      : 'text-gray-800 dark:text-white'
                                  }`}>
                                    {item.itemName} x {item.quantity}
                                  </p>
                                  {(item.requestedSpiceLevel || item.spiceLevel) && (
                                    <SpiceLevelBadge
                                      spiceLevel={item.requestedSpiceLevel || item.spiceLevel}
                                      size="small"
                                    />
                                  )}
                                </div>
                                {item.specialInstructions && (
                                  <p className={`text-sm italic ${
                                    item.status === 'CANCELLED'
                                      ? 'text-red-400'
                                      : 'text-gray-600 dark:text-gray-400'
                                  }`}>
                                    {item.specialInstructions}
                                  </p>
                                )}
                              </div>
                              <span className={`text-sm font-medium ${
                                item.status === 'CANCELLED' ? 'text-red-600' :
                                item.status === 'READY' ? 'text-green-600' :
                                item.status === 'PREPARING' ? 'text-orange-600' :
                                'text-gray-600'
                              }`}>
                                {item.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Special Instructions */}
                      {kot.specialInstructions && (
                        <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900 rounded">
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            Special Instructions:
                          </p>
                          <p className="text-sm text-yellow-700 dark:text-yellow-300">
                            {kot.specialInstructions}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
              <p className="text-gray-500 dark:text-gray-400">No KOTs generated for this order yet.</p>
            </div>
          )}
        </section>

        {/* Allergy declaration (UK plan Phase 4) — always highlighted */}
        {order.allergyNote && (
          <section className="mb-6">
            <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border-2 border-red-300 dark:border-red-700">
              <div className="flex items-start gap-2">
                <span className="text-xl">⚠️</span>
                <div>
                  <h3 className="font-bold text-red-800 dark:text-red-300 uppercase text-sm">Allergy</h3>
                  <p className="text-sm font-semibold text-red-700 dark:text-red-400 mt-1">{order.allergyNote}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Order-Level Special Instructions */}
        {order.orderNotes && (
          <section className="mb-6">
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
              <div className="flex items-start gap-2">
                <span className="text-xl">📝</span>
                <div>
                  <h3 className="font-medium text-yellow-800 dark:text-yellow-300">Order Notes</h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">{order.orderNotes}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Items Section (Simple List) */}
        <section className="mb-6">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">All Items</h3>
          {order.items && order.items.length > 0 ? (
            <ul className="space-y-4">
              {order.items.map((item) => (
                <li key={item.id} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {(item as any).imageUrl && (
                          <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                            <MenuItemImage imageUrl={(item as any).imageUrl} itemName={item.menuItemName || item.menuItem?.name || 'Item'} size="thumbnail" />
                          </div>
                        )}
                        <p className="font-semibold text-gray-800 dark:text-white text-lg">
                          {item.quantity}× {item.menuItemName}
                        </p>
                        {item.portionName && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {item.portionName}
                          </span>
                        )}
                        {(item.requestedSpiceLevel || item.spiceLevel) && (
                          <SpiceLevelBadge
                            spiceLevel={item.requestedSpiceLevel || item.spiceLevel}
                            size="small"
                          />
                        )}
                        {item.kotNumber && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            (KOT: {item.kotNumber})
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        <span>{CURRENCY.symbol}{(item.menuItem?.price || item.price || 0).toFixed(2)} each</span>
                        {item.quantity > 1 && (
                          <span className="ml-2">× {item.quantity} = {CURRENCY.symbol}{((item.menuItem?.price || item.price || 0) * item.quantity).toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                    <p className="font-bold text-gray-800 dark:text-white text-lg ml-4">
                      {CURRENCY.symbol}{((item.menuItem?.price || item.price || 0) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                  {item.toppings && item.toppings.length > 0 && (
                    <div className="mt-2 pl-3 border-l-2 border-blue-300 dark:border-blue-600">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Toppings:</p>
                      <ul className="mt-1 space-y-0.5">
                        {item.toppings.map(topping => (
                          <li key={topping.id} className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1">
                            <span className="text-blue-500">•</span>
                            {topping.name}
                            {topping.price && topping.price > 0 && (
                              <span className="text-xs text-gray-500"> (+{CURRENCY.symbol}{topping.price.toFixed(2)})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {item.comment && (
                    <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border-l-2 border-yellow-400 dark:border-yellow-600">
                      <p className="text-xs font-medium text-yellow-800 dark:text-yellow-300">Special Instructions:</p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400 italic mt-0.5">{item.comment}</p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 dark:text-gray-400"><em>No items found in this order.</em></p>
          )}
        </section>

        {/* Update Status Section */}
        <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">Update Order Status</h3>

          {/* Visual Status Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
            {orderStatuses.map((status) => {
              const isSelected = newStatus === status;
              const isCurrent = order.status === status;

              // Define colors for each status (Kitchen Display System inspired)
              const getStatusStyle = (status: string) => {
                const styles = {
                  'PENDING': { bg: '#FEF3C7', border: 'var(--status-warn)', text: '#8A5411', icon: '⏳' },
                  'ACCEPTED': { bg: '#DBEAFE', border: 'var(--status-info-border)', text: '#2D6CB5', icon: '✅' },
                  'IN_PROGRESS': { bg: '#FDE68A', border: 'var(--status-warn)', text: '#8A5411', icon: '⚡' },
                  'PREPARING': { bg: '#FED7AA', border: '#FB923C', text: '#E0891E', icon: '👨‍🍳' }, /* basil-todo */
                  'READY': { bg: '#D1FAE5', border: 'var(--status-ok-border)', text: '#1E8E5A', icon: '🟢' },
                  'COMPLETED': { bg: '#F3F4F6', border: '#6B7280', text: '#4B5563', icon: '✅' }, /* basil-todo */
                  'FAILED': { bg: '#FEE2E2', border: 'var(--status-bad)', text: '#D2453C', icon: '❌' },
                  'CANCELLED': { bg: '#FEE2E2', border: 'var(--status-bad)', text: '#D2453C', icon: '🚫' }
                };
                return styles[status as keyof typeof styles] || styles['PENDING'];
              };

              const statusStyle = getStatusStyle(status);
              const displayName = status.replace('_', ' ');

              return (
                <button
                  key={status}
                  onClick={() => setNewStatus(status)}
                  disabled={actionLoading}
                  className={`relative p-4 rounded-lg border-2 transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${isSelected ? 'ring-2 ring-offset-2' : 'hover:shadow-md'
                    }`}
                  style={{
                    backgroundColor: statusStyle.bg,
                    borderColor: isSelected ? statusStyle.border : isCurrent ? statusStyle.border : 'var(--border-subtle)',
                    borderWidth: isSelected || isCurrent ? '2px' : '1px',
                    transform: isSelected ? 'scale(0.98)' : undefined
                  }}
                >
                  {/* Current Status Badge */}
                  {isCurrent && (
                    <div
                      className="absolute -top-2 -right-2 px-2 py-1 text-xs font-bold rounded-full text-white"
                      style={{ backgroundColor: statusStyle.border }}
                    >
                      CURRENT
                    </div>
                  )}

                  {/* Status Icon */}
                  <div className="text-2xl mb-2 text-center">
                    {statusStyle.icon}
                  </div>

                  {/* Status Text */}
                  <div
                    className="text-sm font-semibold text-center"
                    style={{ color: statusStyle.text }}
                  >
                    {displayName}
                  </div>

                  {/* Selection Indicator */}
                  {isSelected && (
                    <div
                      className="absolute bottom-1 right-1 w-3 h-3 rounded-full"
                      style={{ backgroundColor: statusStyle.border }}
                    ></div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Update Button */}
          <div className="flex justify-center">
            <button
              onClick={handleStatusUpdate}
              disabled={actionLoading || newStatus === order.status}
              className="px-8 py-3 font-medium rounded-lg shadow-lg transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: newStatus !== order.status ? theme.button.primary.bg : theme.components.input.border,
                color: newStatus !== order.status ? theme.button.primary.text : theme.text,
              }}
            >
              {actionLoading ? 'Updating...' : newStatus !== order.status ? `Update to ${newStatus.replace('_', ' ')}` : 'Select Different Status'}
            </button>
          </div>

          {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400 text-center">{error}</p>}
        </section>
      </div>
    );
  };

  return (
    <>
      <CardLayoutTheme
        title="Vendor Order Detail"
        categories={[{ id: 1, name: 'Order Management' }]} // Dummy category
        renderItems={renderVendorOrderContent}
        hideSidebar={true}
        sidebarTitle="Order Information"
      />
      {/* Toaster provided globally via ToastProvider */}
    </>
  );
};

export default VendorOrderDetail;

